'use client';

import React, { useMemo } from 'react';
import { useProfile } from 'src/context/providers/ProfileProvider';

interface AffiliationItem {
  id: string;
  name: string;
  type: string;
}

const ProfileAffiliationsPreview: React.FC = () => {
  const { entityDetails } = useProfile();

  const items = useMemo(() => {
    if (!entityDetails) return [];

    const systems: AffiliationItem[] = entityDetails.systems || [];
    const groups: AffiliationItem[] = entityDetails.groups || [];
    const affiliations: AffiliationItem[] = entityDetails.affiliations || [];

    // GPO/EHR affiliations are surfaced on the site of care Affiliations card, so the
    // preview must include them too.
    const isSiteOfCare = entityDetails.type === 'site_of_care';
    const gpos: AffiliationItem[] = isSiteOfCare
      ? entityDetails.gpos || []
      : [];
    const ehrs: AffiliationItem[] = isSiteOfCare
      ? entityDetails.ehrs || []
      : [];

    return [...systems, ...groups, ...affiliations, ...gpos, ...ehrs].slice(
      0,
      3
    );
  }, [entityDetails]);

  if (items.length === 0) {
    return (
      <div
        className="flex items-center justify-center py-4"
        data-testid="profile-affiliations-preview-empty"
      >
        <p className="text-sm text-neutral-500">No data available</p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col"
      data-testid="profile-affiliations-preview"
      role="list"
      aria-label="Affiliations"
    >
      {items.map((item, i) => (
        <div
          key={`${item.id}-${i}`}
          className="flex items-center py-1.5 border-b border-neutral-100 last:border-b-0"
          role="listitem"
        >
          <span className="text-sm font-medium text-primary truncate">
            {item.name}
          </span>
        </div>
      ))}
    </div>
  );
};

export default ProfileAffiliationsPreview;
